import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  type: 'rank' | 'coins' | 'cosmetic' | 'crate';
  image?: string;
  duration?: number; // Duration in months for ranks
  basePrice?: number; // Original base price for duration calculations
}

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  totalCoins: number;
  totalDollars: number;
  addToCart: (item: Omit<CartItem, 'quantity'>) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  isCartOpen: boolean;
  toggleCart: () => void;
  closeCart: () => void;
  isCheckoutOpen: boolean;
  openCheckout: () => void;
  closeCheckout: () => void;
  hasRankInCart: (rankId: string) => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  // Separate totals for coins (in-game currency) and dollars (real money)
  const totalCoins = items
    .filter(item => item.type !== 'coins')
    .reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  const totalDollars = items
    .filter(item => item.type === 'coins')
    .reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Check if a specific rank is already in the cart
  const hasRankInCart = (rankSlug: string): boolean => {
    return items.some(item => item.type === 'rank' && item.id.startsWith(rankSlug + '-'));
  };

  const addToCart = (newItem: Omit<CartItem, 'quantity'>) => {
    setItems((prevItems) => {
      // For ranks, don't allow duplicates or quantity > 1
      if (newItem.type === 'rank') {
        const existingRank = prevItems.find((item) => item.id === newItem.id);
        if (existingRank) {
          // Already in cart, don't add again
          return prevItems;
        }
        // Add rank with quantity locked to 1
        return [...prevItems, { ...newItem, quantity: 1 }];
      }
      
      // For other items, allow quantity increase
      const existingItem = prevItems.find((item) => item.id === newItem.id);
      if (existingItem) {
        return prevItems.map((item) =>
          item.id === newItem.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevItems, { ...newItem, quantity: 1 }];
    });
  };

  const removeFromCart = (itemId: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    // For ranks, quantity is always 1
    const item = items.find(i => i.id === itemId);
    if (item?.type === 'rank') {
      return; // Don't allow quantity change for ranks
    }
    
    setItems((prevItems) =>
      prevItems.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const toggleCart = () => {
    setIsCartOpen((prev) => !prev);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const openCheckout = () => {
    setIsCheckoutOpen(true);
    setIsCartOpen(false);
  };

  const closeCheckout = () => {
    setIsCheckoutOpen(false);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalPrice,
        totalCoins,
        totalDollars,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        toggleCart,
        closeCart,
        isCheckoutOpen,
        openCheckout,
        closeCheckout,
        hasRankInCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
